/**
 * Phase 3F: Final Sequencing Panel
 * 
 * Enhanced panel showing:
 * - Reimbursement-priority sequencing
 * - Multiple procedure reductions
 * - Modifier explanations with evidence linking
 * - LINK Ops override capability
 * - Export-ready validation
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  DollarSign,
  ArrowUpDown,
  AlertTriangle,
  CheckCircle2,
  Info,
  Edit3,
  RotateCcw,
  ShieldCheck,
  Ban,
  HelpCircle,
  Sparkles,
  FileText,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useFinalCptSequencing, type FinalCptLine } from '@/hooks/useFinalCptSequencing';
import type { BillableReconciliationItem } from '@/lib/phase3/cptSequencingEngine';
import type { SiteOfService } from '@/lib/modifiers/types';

interface FinalSequencingPanelProps {
  authorizationId: string | null;
  billableItems: BillableReconciliationItem[];
  siteOfService?: SiteOfService;
  payerId?: string;
  planId?: string;
  onExport?: () => void;
  /** Called when sequencing completes with final lines and result */
  onSequencingComplete?: (finalLines: FinalCptLine[], result: import('@/lib/phase3/cptSequencingEngine').SequencingResult) => void;
  userRole?: 'link_ops' | 'surgeon' | 'hospital_admin';
}

export function FinalSequencingPanel({
  authorizationId,
  billableItems,
  siteOfService = 'hospital',
  payerId,
  planId,
  onExport,
  onSequencingComplete,
  userRole = 'link_ops',
}: FinalSequencingPanelProps) {
  const { isDemoMode } = useDemoMode();
  const [auditOpen, setAuditOpen] = useState(false);
  
  const {
    sequencingResult,
    finalLines,
    loading,
    error,
    canExport,
    totalReimbursement,
    buildSequence,
    overrideModifiers,
    revertOverride,
    acknowledgeWarning,
  } = useFinalCptSequencing({
    authorizationId,
    billableItems,
    siteOfService,
    payerId,
    planId,
    onExportReady: () => {
      console.log('Export ready!');
    },
  });

  const [overrideDialog, setOverrideDialog] = useState<{
    isOpen: boolean;
    line: FinalCptLine | null;
    newModifiers: string;
    reason: string;
  }>({
    isOpen: false,
    line: null,
    newModifiers: '',
    reason: '',
  });

  const [revertDialog, setRevertDialog] = useState<{
    isOpen: boolean;
    lineId: string;
    reason: string;
  }>({
    isOpen: false,
    lineId: '',
    reason: '',
  });

  // Rebuild when billable items change
  useEffect(() => {
    if (billableItems.length > 0) {
      buildSequence(billableItems);
    }
  }, [billableItems, buildSequence]);

  // Notify parent when sequencing completes
  useEffect(() => {
    if (sequencingResult && finalLines.length > 0) {
      onSequencingComplete?.(finalLines, sequencingResult);
    }
  }, [sequencingResult, finalLines, onSequencingComplete]);

  const canOverride = userRole === 'link_ops';

  const openOverrideDialog = (line: FinalCptLine) => {
    setOverrideDialog({
      isOpen: true,
      line,
      newModifiers: line.modifiers.join(', '),
      reason: '',
    });
  };

  const handleOverrideSubmit = async () => {
    if (!overrideDialog.line || !overrideDialog.reason.trim()) return;
    
    const newMods = overrideDialog.newModifiers
      .split(',')
      .map(m => m.trim().toUpperCase())
      .filter(m => m.length > 0);
    
    const success = await overrideModifiers({
      lineId: overrideDialog.line.id,
      cptCode: overrideDialog.line.cptCode,
      newModifiers: newMods,
      reason: overrideDialog.reason,
    });
    
    if (success) {
      setOverrideDialog(prev => ({ ...prev, isOpen: false }));
    }
  };

  const handleRevertSubmit = async () => {
    if (!revertDialog.lineId || !revertDialog.reason.trim()) return;
    
    const success = await revertOverride(revertDialog.lineId, revertDialog.reason);
    if (success) {
      setRevertDialog({ isOpen: false, lineId: '', reason: '' });
    }
  };

  if (!sequencingResult || finalLines.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowUpDown className="h-5 w-5" />
            Final CPT Sequence
          </CardTitle>
          <CardDescription>
            Confirm all CPTs as performed to generate final export lines
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Pending Confirmation</AlertTitle>
            <AlertDescription>
              All billable CPTs must be surgeon-confirmed before sequencing.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const hasBlockingIssues = sequencingResult.hasBlockingIssues && !isDemoMode;
  const linesWithReview = finalLines.filter(l => l.reviewRequired);

  return (
    <TooltipProvider>
      <Card className={cn(
        "border-2",
        canExport ? "border-success/50" : "border-warning/50"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                {canExport ? (
                  <ShieldCheck className="h-5 w-5 text-success" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-warning" />
                )}
                Final CPT Sequence
                {isDemoMode && (
                  <Badge variant="secondary" className="ml-2 text-xs">Demo</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Sequenced by reimbursement priority with modifiers applied
              </CardDescription>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Est. Reimbursement</p>
              <p className="text-2xl font-bold text-success">
                ${totalReimbursement.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Blocking Alert */}
          {hasBlockingIssues && (
            <Alert variant="destructive">
              <Ban className="h-4 w-4" />
              <AlertTitle>Export Blocked</AlertTitle>
              <AlertDescription>
                {linesWithReview.length} CPT(s) require review before export.
                {canOverride && ' Acknowledge warnings or override to proceed.'}
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Sequenced Lines Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[50px]">Seq</TableHead>
                  <TableHead className="w-[90px]">CPT</TableHead>
                  <TableHead>Modifiers</TableHead>
                  <TableHead>Final Code</TableHead>
                  <TableHead className="text-right w-[90px]">Base $</TableHead>
                  <TableHead className="text-center w-[50px]">%</TableHead>
                  <TableHead className="text-right w-[90px]">Eff. $</TableHead>
                  <TableHead className="w-[70px]">Status</TableHead>
                  {canOverride && <TableHead className="w-[70px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {finalLines.map((line) => (
                  <TableRow 
                    key={line.id}
                    className={cn(
                      line.isPrimary && "bg-primary/5",
                      line.reviewRequired && !isDemoMode && "bg-warning/5",
                      line.isOverridden && "bg-accent/10"
                    )}
                  >
                    <TableCell>
                      <Badge 
                        variant={line.isPrimary ? "default" : "secondary"}
                        className={cn("font-mono", line.isPrimary && "bg-primary")}
                      >
                        {line.sequence}
                      </Badge>
                    </TableCell>

                    <TableCell className="font-mono font-medium">
                      {line.cptCode}
                    </TableCell>

                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {line.modifiers.length === 0 ? (
                          <span className="text-muted-foreground text-xs">None</span>
                        ) : (
                          line.modifiers.map((mod) => (
                            <ModifierTooltip
                              key={mod}
                              modifier={mod}
                              explanation={line.modifierExplanations[mod]}
                              isOverridden={line.isOverridden}
                            />
                          ))
                        )}
                      </div>
                    </TableCell>

                    <TableCell>
                      <code className="px-2 py-1 rounded bg-muted font-mono text-sm">
                        {line.finalCodeString}
                      </code>
                      {line.isOverridden && (
                        <Badge variant="outline" className="ml-2 text-xs bg-accent/20">
                          Modified
                        </Badge>
                      )}
                    </TableCell>

                    <TableCell className="text-right font-mono text-sm">
                      ${line.baseReimbursement.toFixed(2)}
                    </TableCell>

                    <TableCell className="text-center">
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "font-mono text-xs",
                          line.isPrimary ? "text-success" : "text-muted-foreground"
                        )}
                      >
                        {line.isPrimary ? '100%' : '50%'}
                      </Badge>
                    </TableCell>

                    <TableCell className="text-right font-mono font-medium">
                      ${line.effectiveReimbursement.toFixed(2)}
                    </TableCell>

                    <TableCell>
                      {line.reviewRequired && !isDemoMode ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Review
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <ul className="list-disc list-inside text-xs">
                              {line.reviewReasons.map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Badge variant="secondary" className="text-xs bg-success/10 text-success">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Ready
                        </Badge>
                      )}
                    </TableCell>

                    {canOverride && (
                      <TableCell>
                        <div className="flex gap-1">
                          {line.isOverridden ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2"
                                  onClick={() => setRevertDialog({
                                    isOpen: true,
                                    lineId: line.id,
                                    reason: '',
                                  })}
                                >
                                  <RotateCcw className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Revert override</TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2"
                                  onClick={() => openOverrideDialog(line)}
                                >
                                  <Edit3 className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Override modifiers</TooltipContent>
                            </Tooltip>
                          )}
                          {line.reviewRequired && !isDemoMode && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2"
                                  onClick={() => acknowledgeWarning(line.id)}
                                >
                                  <CheckCircle2 className="h-3 w-3 text-success" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Acknowledge warning</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Audit Trail Collapsible */}
          <Collapsible open={auditOpen} onOpenChange={setAuditOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Sequencing Audit Trail ({sequencingResult.auditTrail.length} entries)
                </span>
                {auditOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 p-3 rounded bg-muted/30 max-h-48 overflow-y-auto">
                <ul className="space-y-1 text-xs font-mono">
                  {sequencingResult.auditTrail.map((entry, i) => (
                    <li key={i} className="text-muted-foreground">
                      <span className="text-foreground font-medium">[{entry.action}]</span>{' '}
                      {entry.cptCode !== 'ALL' && <span className="text-primary">{entry.cptCode}:</span>} {entry.details}
                    </li>
                  ))}
                </ul>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Export Action */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {canExport ? (
                <span className="text-success flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" />
                  Ready for export
                </span>
              ) : (
                <span className="text-warning flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  {linesWithReview.length} issue(s) to resolve
                </span>
              )}
            </div>
            <Button
              onClick={onExport}
              disabled={!canExport || loading}
              className="gap-2"
            >
              {isDemoMode && <Sparkles className="h-4 w-4" />}
              Proceed to Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Override Dialog */}
      <Dialog 
        open={overrideDialog.isOpen} 
        onOpenChange={(open) => setOverrideDialog(prev => ({ ...prev, isOpen: open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override Modifiers for CPT {overrideDialog.line?.cptCode}</DialogTitle>
            <DialogDescription>
              Enter the new modifier sequence and provide a reason. This action is logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Modifiers</Label>
              <div className="flex gap-1">
                {overrideDialog.line?.modifiers.map(mod => (
                  <Badge key={mod} variant="secondary">{mod}</Badge>
                ))}
                {(!overrideDialog.line?.modifiers || overrideDialog.line.modifiers.length === 0) && (
                  <span className="text-muted-foreground text-sm">None</span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-modifiers">New Modifiers (comma-separated)</Label>
              <Input
                id="new-modifiers"
                value={overrideDialog.newModifiers}
                onChange={(e) => setOverrideDialog(prev => ({ ...prev, newModifiers: e.target.value }))}
                placeholder="e.g., RT, 59"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="override-reason">Reason (required)</Label>
              <Textarea
                id="override-reason"
                value={overrideDialog.reason}
                onChange={(e) => setOverrideDialog(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Explain why the override is necessary..."
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog(prev => ({ ...prev, isOpen: false }))}>
              Cancel
            </Button>
            <Button onClick={handleOverrideSubmit} disabled={!overrideDialog.reason.trim() || loading}>
              Apply Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert Dialog */}
      <Dialog 
        open={revertDialog.isOpen} 
        onOpenChange={(open) => !open && setRevertDialog({ isOpen: false, lineId: '', reason: '' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revert Override</DialogTitle>
            <DialogDescription>
              Restore the original modifier sequence. This action is logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="revert-reason">Reason for Reverting (required)</Label>
              <Textarea
                id="revert-reason"
                value={revertDialog.reason}
                onChange={(e) => setRevertDialog(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Explain why you're reverting to original..."
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertDialog({ isOpen: false, lineId: '', reason: '' })}>
              Cancel
            </Button>
            <Button onClick={handleRevertSubmit} disabled={!revertDialog.reason.trim() || loading}>
              Revert to Original
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

// ============================================================================
// MODIFIER TOOLTIP COMPONENT
// ============================================================================

interface ModifierTooltipProps {
  modifier: string;
  explanation?: string;
  isOverridden?: boolean;
}

function ModifierTooltip({ modifier, explanation, isOverridden }: ModifierTooltipProps) {
  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <Badge 
          variant="outline" 
          className={cn(
            "font-mono cursor-help text-xs",
            isOverridden && "border-accent bg-accent/20"
          )}
        >
          {modifier}
          <HelpCircle className="h-3 w-3 ml-1 text-muted-foreground" />
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent className="w-72">
        <div className="space-y-2">
          <h4 className="font-semibold text-sm">Why Modifier {modifier}?</h4>
          <p className="text-sm text-muted-foreground">
            {explanation || 'Applied based on procedural rules'}
          </p>
          {isOverridden && (
            <p className="text-xs text-accent-foreground bg-accent/20 p-2 rounded">
              This modifier was manually applied via override.
            </p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
